import { FC, PropsWithChildren, useMemo } from 'react';

import { liveFeedbackMeasurementsAtom } from '@realtime';
import { atom, useAtomValue } from 'jotai';

import { useMergedCorridors } from '@blocks';
import { simpleConvert, Unit } from '@core';
import { useTranslate } from '@i18n';
import { useUiParameters } from '@queries';
import { userSettingsAtom } from '@store';
import { Box, colors, GoalIcon, LiveAnalysisParameterCard, Text, u } from '@ui';

import * as css from './styles.css';

const eventParametersAtom = atom((get) => {
    const lm = get(liveFeedbackMeasurementsAtom);
    return lm?.measurements.map((m) => [m.par[0], m.val] as const);
});

export const ParameterContainer: FC<PropsWithChildren> = ({ children }) => {
    return (
        <Box mt={u(2)} className={css.parameterContainer}>
            {children}
        </Box>
    );
};

type ParameterDisplayProps = {
    min: number;
    max: number;
    label: string;
    unit: Unit;
    value?: number;
};

export const ParameterDisplay: FC<ParameterDisplayProps> = ({ min, max, label, value, unit }) => {
    const userSettings = useAtomValue(userSettingsAtom);

    const displayValue = simpleConvert({
        value: value,
        unit,
        userPreferredUnit: userSettings.userPreferredUnit,
    });

    let color: 'success' | 'danger' | undefined;
    if (displayValue !== null && typeof displayValue !== 'undefined') {
        color = displayValue >= min && displayValue <= max ? 'success' : 'danger';
    }
    return (
        <Box
            display="flex"
            alignItems="stretch"
            height="100%"
            gap={u(2)}
            style={{ minWidth: 302, position: 'relative' }}
        >
            <Box
                className={css.parameterBar}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                alignItems="center"
            >
                <Text variant="h4">{max}</Text>
                <GoalIcon size={14} color={colors.white} />
                <Text variant="h4">{min}</Text>
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="space-between">
                <Text variant="h3">{label}</Text>
                <Box display="flex" flexDirection="column" justifyContent="center" flexGrow={1}>
                    <Text variant="h1" color={color}>
                        {displayValue ?? '-'}
                    </Text>
                </Box>
            </Box>
        </Box>
    );
};

export const LiveAnalysisParameters = () => {
    const { data: uiParameters } = useUiParameters();
    const mergedCorridors = useMergedCorridors();
    const uiParameterMap = useMemo(
        () => Object.fromEntries(uiParameters?.map((node) => [node.parameter.smpId, node] as const) ?? []),
        [uiParameters],
    );

    const eventParameters = useAtomValue(eventParametersAtom);

    const t = useTranslate();
    const userSettings = useAtomValue(userSettingsAtom);

    return (
        <ParameterContainer>
            {eventParameters?.map(([id, value]) => {
                const uiNode = uiParameterMap[id];
                if (!uiNode) {
                    console.error('No node found for [smpId]:', id);
                    return null;
                }
                const corridors = mergedCorridors[uiNode.parameter.smpId];
                if (!corridors) {
                    console.error('No corridors found for [nodeId]:', uiNode.id);
                    return null;
                }
                return (
                    <LiveAnalysisParameterCard
                        key={id}
                        title={t(uiNode?.i18n ?? '-')}
                        value={value}
                        unit={corridors.unit}
                        userPreferredUnit={userSettings.userPreferredUnit}
                        goalCorridor={{
                            innerMax: corridors.max,
                            innerMin: corridors.min,
                        }}
                    />
                );
            })}
        </ParameterContainer>
    );
};
